import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useRequestData, usePrefs } from 'wsm-common-data';
import { PricingDetail, InstantEpriceContainer } from 'wsm-pricing-display';
import { useDealerOptions } from 'wsm-pricing-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { SRP, VDP } from '../constants/constants';

import '../global-sass/main.scss';

const Widget = () => {
	const { deviceType, widgetName, locale } = useRequestData();
	const prefs = usePrefs();
	const {
		account = { name: '' },
		pricingModuleData = {},
		userProfile,
		widgetInfo: { pageAlias }
	} = useSelector((state) => state);

	const [isDisplayingEPrice, setIsDisplayingEPrice] = useState(false);
	const [isScrollingSidebar, setIsScrollingSidebar] = useState(false);
	const [layout, setLayout] = useState(SRP);
	const [renderPortal, setRenderPortal] = useState(false);
	const [shouldRequestDealerOptions, setShouldRequestDealerOptions] =
		useState(false);
	const setUserProfile = useDispatch();
	const dealerOptions = useDealerOptions(
		Object.keys(pricingModuleData).length
			? pricingModuleData.vehicleId
			: null,
		locale,
		shouldRequestDealerOptions,
		widgetName
	);

	const handleDealerOptionsMouseEnter = () => {
		setShouldRequestDealerOptions(true);
	};

	const trackScrollingSidebar = useCallback(() =>
		// Tracks whether the sidebar is scrolling or not based on the height set
		{
			if (layout === 'VDP') {
				setIsScrollingSidebar(
					document.querySelector('.vdp-sidebar').style.top.length > 0
				);
			}
		}, [layout]);

	const showEPrice = (id, vehicleId) => {
		if (id === vehicleId && id !== 'none') {
			setIsDisplayingEPrice(true);
		}
	};

	useEffect(() => {
		if (deviceType === 'DESKTOP') {
			window.addEventListener('scroll', trackScrollingSidebar);
		}

		if (
			window &&
			window.document &&
			window.document
				.getElementsByTagName('html')[0]
				.className.includes(VDP.toLowerCase())
		) {
			setLayout(VDP);
		}

		const pubsub =
			(typeof window !== 'undefined' &&
				window.DDC &&
				window.DDC.pubsub) ||
			undefined;

		if (pubsub) {
			pubsub.subscribe('ws-vehicle-ctas/render-eprice-portal', () => {
				setRenderPortal(true);
			});

			pubsub.subscribe('instant-ePrice-completed', () => {
				let profile = window.DDC.userProfile;
				if (!(profile && Object.keys(profile).length > 0)) {
					profile = null;
				}
				setUserProfile({
					type: 'SET_USER_PROFILE',
					payload: { userProfile: profile }
				});
			});
		}

		return () => {
			if (deviceType === 'DESKTOP') {
				window.removeEventListener('scroll', trackScrollingSidebar);
			}
		};
	}, [deviceType, setUserProfile, trackScrollingSidebar]);

	if (Object.keys(pricingModuleData).length > 0) {
		const {
			incentives = {},
			condition,
			vehicleId,
			inventoryData,
			accountId,
			vehicleAccountId
		} = pricingModuleData;

		const {
			dprice,
			eprice,
			loan,
			lease,
			retailPrice,
			ePriceStatus,
			ePriceButtonLabel
		} = inventoryData;

		const componentPricing = {
			dPrice: dprice,
			ePrice: eprice,
			loan,
			lease,
			retailPrice,
			ePriceStatus,
			ePriceButtonLabel
		};

		const [ePriceContainer] =
			typeof document !== 'undefined'
				? document.querySelectorAll('.eprice-react-portal')
				: '';

		const ePriceButton = !isDisplayingEPrice ? (
			<InstantEpriceContainer
				accountId={accountId}
				buttonSize={prefs.buttonSizeEPrice}
				buttonStyle={prefs.buttonStyleEPrice}
				ePriceFontWeight={prefs.ePriceFontWeight}
				instantEpricePreventMultipleEpriceLeads={
					prefs.instantEpricePreventMultipleEpriceLeads
				}
				instantEpriceShowAllAfterLeadSubmit={
					prefs.instantEpriceShowAllAfterLeadSubmit
				}
				pageAlias={pageAlias}
				pricing={componentPricing}
				showEPrice={(id) => showEPrice(id, vehicleId)}
				showInstantEpriceForSharedInventory={
					prefs.showInstantEpriceForSharedInventory
				}
				userProfile={userProfile}
				vehicleId={vehicleId}
				vehicleAccountId={vehicleAccountId}
			/>
		) : null;

		return (
			<ErrorBoundary newRelicPageAction="WS DETAILED PRICING ERROR BOUNDARY">
				<PricingDetail
					accountId={accountId}
					accountInfo={account}
					condition={condition}
					dealerOptions={dealerOptions}
					fetchDealerOptionsCallback={handleDealerOptionsMouseEnter}
					incentives={incentives}
					isDisplayingEPrice={isDisplayingEPrice}
					isScrollingSidebar={isScrollingSidebar}
					layout={layout}
					prefs={prefs}
					pricing={componentPricing}
					vehicleAccountId={vehicleAccountId}
					vehicleId={vehicleId}
				/>
				{ePriceContainer &&
					renderPortal &&
					ReactDOM.createPortal(ePriceButton, ePriceContainer)}
			</ErrorBoundary>
		);
	} else {
		return null;
	}
};

export default Widget;
